/**
 * The rundown editor's side panel (container): the focused row's generic
 * field form, plus the typed piece form for a piece and the script for a
 * part, each change committed as a patch through the live session.
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import type { Row } from '@acp/domain/row.js';
import type { EditorSession } from '../api/rundownEditor.js';
import { Inspector } from '../components/editor/Inspector.js';
import { PieceForm } from '../components/editor/PieceForm.js';
import { ScriptPanel } from '../components/editor/ScriptPanel.js';
import { rebaseEdit } from '../lib/editor/caret.js';
import { fieldControls } from '../lib/editor/fieldForm.js';
import { setPatch, textPatch } from '../lib/editor/patches.js';
import { PIECE_TYPES } from '../lib/editor/pieceTypes.js';
import type { RowPresence } from '../lib/editor/presence.js';
import { pieceView, type RowView, displayName } from '../lib/editor/viewModel.js';

export type EditorInspectorProps = {
  readonly session: EditorSession;
  /** The focused row as the editor shows it, and its view model; null when nothing is focused. */
  readonly focused: { readonly row: Row; readonly view: RowView } | null;
  readonly presence: readonly RowPresence[];
  readonly onFocusField: (path: string | null) => void;
};

/** A piece's `params.*` leaves, keyed by the leaf path under `params`. */
export function paramsOf(f: Readonly<Record<string, JsonValue>>): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {};
  for (const [path, value] of Object.entries(f)) if (path.startsWith('params.')) out[path.slice('params.'.length)] = value;
  return out;
}

function PieceSection({ session, row, onFocusField }: { readonly session: EditorSession; readonly row: Row; readonly onFocusField: (path: string | null) => void }) {
  const view = pieceView(row);
  const commit = (set: Readonly<Record<string, JsonValue>>) => session.commit([setPatch(session.writer, row.rowId, set)], true);
  return (
    <PieceForm
      types={PIECE_TYPES}
      pieceType={view.pieceType}
      params={paramsOf(row.f)}
      timing={{ start: view.start, duration: view.duration }}
      onTypeChange={(spec) => commit({ pieceType: spec.id, sourceLayer: spec.defaultSourceLayer })}
      onParamCommit={(key, value) => commit({ [`params.${key}`]: value })}
      onTimingCommit={(timing) => commit({ enable: { start: timing.start, duration: timing.duration } })}
      onFocusField={onFocusField}
    />
  );
}

function ScriptSection({ session, row, presence, onFocusField }: { readonly session: EditorSession; readonly row: Row; readonly presence: readonly RowPresence[]; readonly onFocusField: (path: string | null) => void }) {
  return (
    <ScriptPanel
      text={typeof row.f['script'] === 'string' ? row.f['script'] : ''}
      editors={presence.filter((p) => p.activity === 'editing' && p.field === 'script')}
      onChange={(prev, next) => {
        // The row as it is NOW: a remote merge may have landed since the textarea last showed it.
        const current = session.store.get(row.rowId);
        if (current === null) return;
        const now = typeof current.f['script'] === 'string' ? current.f['script'] : '';
        const patch = textPatch(session.writer, current, 'script', rebaseEdit(prev, next, now));
        if (patch !== null) session.commit([patch], true);
      }}
      onFocusChange={(on) => onFocusField(on ? 'script' : null)}
      onUndo={() => session.undo()}
      onRedo={() => session.redo()}
    />
  );
}

export function EditorInspector({ session, focused, presence, onFocusField }: EditorInspectorProps) {
  if (focused === null) return <Inspector kind={null} heading="" controls={[]} presence={[]} onCommit={() => undefined} onFocusField={onFocusField} />;
  const { row, view } = focused;
  return (
    <Inspector
      key={row.rowId}
      kind={row.kind}
      heading={displayName(view)}
      controls={fieldControls(row.kind, row.f)}
      presence={presence}
      onCommit={(path, value) => session.commit([setPatch(session.writer, row.rowId, { [path]: value })], true)}
      onFocusField={onFocusField}
    >
      {row.kind === 'piece' ? <PieceSection session={session} row={row} onFocusField={onFocusField} /> : null}
      {row.kind === 'part' ? <ScriptSection session={session} row={row} presence={presence} onFocusField={onFocusField} /> : null}
    </Inspector>
  );
}
